// ─── AutoChinBot · Task 5 — "Risk Control" ─────────────────────
// The RISK GATE that must pass BEFORE any order is sent to cTrader.
// Adapted from the Chin Bot risk controller, but designed for the AutoChinBot
// architecture (a module-level singleton that runs in the background, not
// bound to any UI screen, and reachable by the future order-send path).
//
// Four rules (all copied from Chin Bot):
//
//   Rule 1 — Spread: current spread must be ≤ 3 pips.
//            NOT applied to XAU…, XAG…, XTI… (metals / oil).
//   Rule 2 — Max Orders: a symbol's open position count must stay < maxOrders.
//            maxOrders is NOT a UI column — it is delivered through the
//            user↔bot Telegram conversation (stored in workspace.symbolConfigs).
//   Rule 3 — Single Symbol: only ONE symbol may hold open positions at a time.
//   Rule 4 — After Hours: order sending is blocked in the daily window
//            23:00 GMT+7 → 06:00 GMT+7 (next day) = 16:00 → 23:00 UTC.
//            The engine keeps running in the background (unlike Chin Bot,
//            which shut engines down); we only block the ORDER path.
//
// Behaviour:
//   • Every check is written to the System Log (so the user can see the gate
//     working on every closed candle AND on demand before an order).
//   • This controller NEVER pushes Telegram by itself. The periodic candle-close
//     check only LOGS. A single open position blocks EVERY other symbol (Rule 3),
//     so notifying per symbol on each candle close would spam one "blocked"
//     message per symbol. A risk-block alert is sent ONCE per REAL order attempt
//     by the order paths themselves (task6 `block-order` / pending
//     `pause-pending-alert`).
//
// Public gate: `canPlaceOrder(symbolId)` → { allowed, reason }.

import { getPipSize } from '@/lib/pip-calculator';
import { getAccountData } from '@/lib/ctrader-gateway-client';
import type { AutoChinBotEngine } from '@/lib/auto-chin-bot-engine';
import type { AutoChinEdataEngine } from '@/lib/autochin-edata-engine';
import type { AutoChinLogInput } from '@/lib/autochin-system-log';
import type { AutoChinTaskEvent } from '@/lib/autochin-etask-engine';

// ─── Types ──────────────────────────────────────────────────────

export interface AutoChinRiskPosition {
  positionId: number;
  symbolId: number;
  symbolName: string;
  tradeSide: 'BUY' | 'SELL';
  volume: number;
}

export interface AutoChinRiskSpreadInfo {
  symbolId: number;
  symbolName: string;
  bid: number | null;
  ask: number | null;
  spreadPips: number | null;
}

export interface AutoChinRiskConfig {
  /**
   * Rule 2 — max concurrent open positions for this symbol.
   * Delivered via the user↔bot Telegram conversation. 0 / undefined = no limit.
   */
  maxOrders?: number;
}

export type AutoChinRiskBlock =
  | 'none'
  | 'spread'
  | 'maxOrders'
  | 'singleSymbol'
  | 'afterHours'
  | 'notReady';

export interface AutoChinRiskResult {
  symbolId: number;
  allowed: boolean;
  blockedBy: AutoChinRiskBlock;
  reason: string;
}

export interface AutoChinRiskEvaluation {
  checks: Record<number, AutoChinRiskResult>;
  /** Symbols that currently hold open positions. */
  symbolsWithPositions: number[];
  /** Open position count per symbol. */
  positionCountBySymbol: Record<number, number>;
  /** True while inside the daily after-hours window. */
  afterHours: boolean;
  at: number;
}

// ─── Constants ──────────────────────────────────────────────────

const MAX_SPREAD_PIPS = 3;

/** 23:00 GMT+7 → 16:00 UTC. */
const AFTER_HOURS_START_UTC_HOUR = 16;
/** 06:00 GMT+7 (next day) → 23:00 UTC. */
const AFTER_HOURS_END_UTC_HOUR = 23;

/** Position snapshot refresh cadence (price/spread is realtime from the engine). */
const POSITION_POLL_INTERVAL_MS = 60_000;

// ─── Pure Functions ─────────────────────────────────────────────

/** Rule 1 target exception: metals / oil never get the spread limit. */
export function isMetalsSymbol(symbolName: string): boolean {
  const upper = symbolName.toUpperCase();
  return upper.startsWith('XAU') || upper.startsWith('XAG') || upper.startsWith('XTI');
}

/** Spread in pips for a symbol (null when quotes are not available yet). */
export function calcSpreadPips(
  bid: number | null,
  ask: number | null,
  symbolName: string,
  pipPosition?: number | null,
): number | null {
  if (bid === null || ask === null || bid <= 0 || ask <= 0) return null;
  const rawSpread = ask - bid;
  if (rawSpread <= 0) return 0;
  return rawSpread / getPipSize(symbolName, pipPosition);
}

/**
 * Rule 4 — true when the current UTC time is inside the daily blocked window
 * [16:00 UTC, 23:00 UTC) = [23:00 GMT+7, 06:00 GMT+7 next day).
 */
export function isAfterHours(now: Date = new Date()): boolean {
  const h = now.getUTCHours();
  return h >= AFTER_HOURS_START_UTC_HOUR && h < AFTER_HOURS_END_UTC_HOUR;
}

function block(symbolId: number, blockedBy: AutoChinRiskBlock, reason: string): AutoChinRiskResult {
  return { symbolId, allowed: false, blockedBy, reason };
}

/**
 * Evaluate all four rules for every active symbol. Pure — no side effects.
 */
export function evaluateAutoChinRisk(
  positions: AutoChinRiskPosition[],
  spreadInfo: Record<number, AutoChinRiskSpreadInfo>,
  configs: Record<number, AutoChinRiskConfig>,
  activeSymbolIds: number[],
  now: Date = new Date(),
): AutoChinRiskEvaluation {
  // Count open positions per symbol.
  const positionCountBySymbol: Record<number, number> = {};
  for (const pos of positions) {
    positionCountBySymbol[pos.symbolId] = (positionCountBySymbol[pos.symbolId] || 0) + 1;
  }
  const symbolsWithPositions = Object.keys(positionCountBySymbol)
    .map(Number)
    .filter((id) => (positionCountBySymbol[id] || 0) > 0);

  const hasAnyPosition = symbolsWithPositions.length > 0;
  const afterHours = isAfterHours(now);
  const checks: Record<number, AutoChinRiskResult> = {};

  for (const symId of activeSymbolIds) {
    const cfg = configs[symId] || {};
    const spread = spreadInfo[symId];
    const symbolName = spread?.symbolName || `#${symId}`;
    const posCount = positionCountBySymbol[symId] || 0;
    const hasPositions = posCount > 0;
    const maxOrders = cfg.maxOrders ?? 0;

    // Rule 4 — After Hours (blocks everything).
    if (afterHours) {
      checks[symId] = block(
        symId,
        'afterHours',
        'Ngoài giờ giao dịch (chặn vào lệnh 23:00–06:00 GMT+7)',
      );
      continue;
    }

    // Rule 3 — Single symbol (other symbol already holds positions).
    if (hasAnyPosition && !hasPositions) {
      const others = symbolsWithPositions
        .map((sid) => spreadInfo[sid]?.symbolName || `#${sid}`)
        .join(', ');
      checks[symId] = block(
        symId,
        'singleSymbol',
        `Đang có vị thế ở ${others} — chỉ cho phép 1 symbol tại 1 thời điểm`,
      );
      continue;
    }

    // Rule 1 — Spread (skipped for metals / oil).
    const skipSpread = isMetalsSymbol(symbolName);
    if (!skipSpread && spread?.spreadPips != null && spread.spreadPips > MAX_SPREAD_PIPS) {
      checks[symId] = block(
        symId,
        'spread',
        `Spread quá cao: ${spread.spreadPips.toFixed(1)} pips (tối đa ${MAX_SPREAD_PIPS})`,
      );
      continue;
    }

    // Rule 2 — Max orders (per symbol, delivered via Telegram conversation).
    if (hasPositions && maxOrders > 0 && posCount >= maxOrders) {
      checks[symId] = block(
        symId,
        'maxOrders',
        `Đã đạt số lệnh tối đa: ${posCount}/${maxOrders}`,
      );
      continue;
    }

    checks[symId] = { symbolId: symId, allowed: true, blockedBy: 'none', reason: 'OK' };
  }

  return { checks, symbolsWithPositions, positionCountBySymbol, afterHours, at: now.getTime() };
}

// ─── Controller ─────────────────────────────────────────────────

export class AutoChinRiskController {
  private bot: AutoChinBotEngine;
  private edata: AutoChinEdataEngine;
  private userId: string;
  private strategyId: string;
  private accountId: string;

  private configs: Record<number, AutoChinRiskConfig> = {};
  private positions: AutoChinRiskPosition[] = [];
  private positionsReady = false;
  private evaluation: AutoChinRiskEvaluation | null = null;

  private logListeners = new Set<(l: AutoChinLogInput) => void>();
  private eventListeners = new Set<(e: AutoChinTaskEvent) => void>();

  private unsubEdata: (() => void) | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private started = false;

  /** Last block type per symbol — used to emit an event only on a state change. */
  private lastBlock: Record<number, AutoChinRiskBlock> = {};

  constructor(
    bot: AutoChinBotEngine,
    edata: AutoChinEdataEngine,
    userId: string,
    strategyId: string,
    accountId: string,
  ) {
    this.bot = bot;
    this.edata = edata;
    this.userId = userId;
    this.strategyId = strategyId;
    this.accountId = accountId;
  }

  // ─── Lifecycle ──────────────────────────────────────────────

  start(): void {
    if (this.started) return;
    this.started = true;

    // Re-check on every closed candle (skip the initial dataset load).
    this.unsubEdata = this.edata.onCandleClose((e) => {
      if (e.initial) return;
      this.check(e.symbolId, 'candle');
    });

    // Keep the position snapshot fresh for the gate.
    void this.fetchPositions();
    this.pollTimer = setInterval(() => { void this.fetchPositions(); }, POSITION_POLL_INTERVAL_MS);
  }

  stop(): void {
    this.started = false;
    if (this.unsubEdata) { this.unsubEdata(); this.unsubEdata = null; }
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
  }

  applyConfigs(configs: Record<number, AutoChinRiskConfig>): void {
    this.configs = { ...configs };
  }

  /**
   * task6 — set/update Rule 2 (maxOrders) for one symbol, merging into the
   * current config so other symbols' limits are preserved. Used when a user
   * delivers a maxOrders value through a /order request over Telegram.
   */
  setMaxOrders(symbolId: number, maxOrders: number): void {
    this.configs = {
      ...this.configs,
      [symbolId]: { ...(this.configs[symbolId] || {}), maxOrders },
    };
  }

  onLog(fn: (l: AutoChinLogInput) => void): () => void {
    this.logListeners.add(fn);
    return () => { this.logListeners.delete(fn); };
  }

  onEvent(fn: (e: AutoChinTaskEvent) => void): () => void {
    this.eventListeners.add(fn);
    return () => { this.eventListeners.delete(fn); };
  }

  getEvaluation(): AutoChinRiskEvaluation | null {
    return this.evaluation;
  }

  getPositions(): AutoChinRiskPosition[] {
    return this.positions;
  }

  isReady(): boolean {
    return this.positionsReady;
  }

  // ─── Public gate — call BEFORE sending any order to cTrader ──

  canPlaceOrder(symbolId: number): AutoChinRiskResult {
    if (!this.positionsReady) {
      return {
        symbolId,
        allowed: false,
        blockedBy: 'notReady',
        reason: 'Đang tải dữ liệu vị thế — chưa thể xác nhận rủi ro',
      };
    }
    return this.check(symbolId, 'order');
  }

  // ─── Internal ───────────────────────────────────────────────

  private activeSymbolIds(): number[] {
    return this.bot.snapshot().symbolIds;
  }

  private buildSpreadInfo(ids: number[]): Record<number, AutoChinRiskSpreadInfo> {
    const snap = this.bot.snapshot();
    const info: Record<number, AutoChinRiskSpreadInfo> = {};
    for (const id of ids) {
      const buf = snap.symbols[id];
      const symbolName = buf?.symbolName || this.edata.get(id)?.symbolName || `#${id}`;
      const bid = buf?.currentBid ?? null;
      const ask = buf?.currentAsk ?? null;
      info[id] = {
        symbolId: id,
        symbolName,
        bid,
        ask,
        spreadPips: calcSpreadPips(bid, ask, symbolName, buf?.pipPosition ?? null),
      };
    }
    return info;
  }

  private evaluate(now: Date = new Date()): {
    evaluation: AutoChinRiskEvaluation;
    spreadInfo: Record<number, AutoChinRiskSpreadInfo>;
  } {
    const ids = this.activeSymbolIds();
    const spreadInfo = this.buildSpreadInfo(ids);
    const evaluation = evaluateAutoChinRisk(this.positions, spreadInfo, this.configs, ids, now);
    this.evaluation = evaluation;
    return { evaluation, spreadInfo };
  }

  /**
   * Run the full evaluation and report the result for ONE symbol.
   * Always logs the check. A `task5_risk_block` event is emitted ONLY when the
   * gate is consulted for a REAL order (`context === 'order'`) — never on the
   * periodic candle-close monitoring, so an open position can't spam one event
   * per symbol. Telegram alerts are sent by the order paths, not here.
   */
  private check(symbolId: number, context: 'candle' | 'order'): AutoChinRiskResult {
    const { evaluation, spreadInfo } = this.evaluate();
    const res: AutoChinRiskResult = evaluation.checks[symbolId] || {
      symbolId,
      allowed: false,
      blockedBy: 'notReady',
      reason: 'Symbol không nằm trong danh sách theo dõi',
    };
    const symbolName = spreadInfo[symbolId]?.symbolName || `#${symbolId}`;
    const posCount = evaluation.positionCountBySymbol[symbolId] || 0;

    // ── Log every check ──────────────────────────────────────
    if (res.allowed) {
      const spreadPips = spreadInfo[symbolId]?.spreadPips;
      const spreadTxt = spreadPips != null ? `${spreadPips.toFixed(1)}p` : '—';
      this.emitLog({
        level: 'info',
        symbolId,
        symbolName,
        message:
          `task5 · ${symbolName} — rủi ro OK ` +
          `(spread ${spreadTxt} · vị thế ${posCount} · ` +
          `${evaluation.symbolsWithPositions.length} symbol đang mở${evaluation.afterHours ? ' · ngoài giờ' : ''})`,
      });
    } else {
      this.emitLog({
        level: 'warn',
        symbolId,
        symbolName,
        message: `task5 · ${symbolName} — ⛔ ${res.reason}`,
      });
    }

    // ── Emit an event ONLY for a real order attempt (task6 / pending fire) ──
    // The periodic candle-close check just logs — it must NOT emit per symbol,
    // otherwise one open position would produce one event per other symbol.
    if (context === 'order' && !res.allowed) {
      if (this.lastBlock[symbolId] !== res.blockedBy) {
        this.lastBlock[symbolId] = res.blockedBy;
        this.emitEvent({
          id: `task5:${symbolId}:${res.blockedBy}:${Math.floor(evaluation.at / 60000)}`,
          type: 'task5_risk_block',
          symbolId,
          symbolName,
          at: Date.now(),
          blockedBy: res.blockedBy,
          riskReason: res.reason,
          message: `⛔ Chặn vào lệnh ${symbolName} — ${res.reason}`,
        });
      }
    } else if (res.allowed) {
      delete this.lastBlock[symbolId];
    }

    return res;
  }

  private async fetchPositions(): Promise<void> {
    const numId = parseInt(this.accountId, 10);
    if (isNaN(numId)) return;

    try {
      const data = await getAccountData(numId);
      if (!data?.success || !data.reconcileRes) return;

      const raw = data.reconcileRes;
      const posArray = Array.isArray(raw.position)
        ? (raw.position as Array<Record<string, unknown>>)
        : [];
      const snap = this.bot.snapshot();

      const mapped: AutoChinRiskPosition[] = posArray.map((p) => {
        const td = (p.tradeData as Record<string, unknown>) || {};
        const volRaw = typeof td.volume === 'number' ? td.volume : 0;
        const sideRaw = typeof td.tradeSide === 'number' ? td.tradeSide : 1;
        const sid = typeof td.symbolId === 'number' ? td.symbolId : 0;
        const symbolName =
          snap.symbols[sid]?.symbolName || this.edata.get(sid)?.symbolName || `#${sid}`;

        return {
          positionId: typeof p.positionId === 'number' ? p.positionId : 0,
          symbolId: sid,
          symbolName,
          tradeSide: (sideRaw === 2 ? 'SELL' : 'BUY') as 'BUY' | 'SELL',
          volume: volRaw / 10_000_000,
        };
      });

      this.positions = mapped;
      this.positionsReady = true;
      // Refresh the stored evaluation quietly (no extra log line).
      this.evaluate();
    } catch {
      /* keep the previous snapshot on failure — never wipe good data */
    }
  }

  // ─── Emit helpers ───────────────────────────────────────────

  private emitLog(input: AutoChinLogInput): void {
    this.logListeners.forEach((fn) => {
      try { fn(input); } catch { /* ignore */ }
    });
  }

  private emitEvent(e: AutoChinTaskEvent): void {
    this.eventListeners.forEach((fn) => {
      try { fn(e); } catch { /* ignore */ }
    });
  }
}